/**
 * The navigation model — one description of every destination in ALFIE.
 *
 * This is the single source of truth for the sidebar, the command palette, the
 * dashboard's tool grid and the breadcrumb trail. Adding a screen means adding
 * one entry here (plus its route in `App.tsx` and its title in `lib/seo.ts`);
 * every surface picks it up automatically.
 *
 * Fields:
 *   to           the route path
 *   label        what the user calls it — matches `lib/seo.ts` PAGE_TITLES
 *   icon         a key of the `Icon` set
 *   description  one line, written for someone deciding whether to click
 *   keywords     extra search terms for the palette (synonyms, old names)
 *   gated        needs a signed-in user (shown locked to anonymous visitors)
 *   staff/admin  role-gated; the server enforces the same rule
 */
import type { IconName } from './icons';

export interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  description?: string;
  keywords?: string[];
  staff?: boolean;
  admin?: boolean;
  /** Requires a signed-in user; shown locked (links to login) for anonymous visitors. */
  gated?: boolean;
}

export interface NavGroup {
  label: string;
  items: NavItem[];
}

/**
 * Groups follow the work, not the build order: where you start (chat, matters),
 * then research, drafting, review, and the case tools. Every tool stays one
 * click away — the grouping is what makes twenty-five of them scannable.
 */
export const TOOL_GROUPS: NavGroup[] = [
  {
    label: 'Workspace',
    items: [
      { to: '/', label: 'Chat', icon: 'Chat', description: 'Ask a question and get a grounded, cited answer.', keywords: ['ask', 'assistant', 'question'] },
      { to: '/dashboard', label: 'Dashboard', icon: 'Dashboard', gated: true, description: 'Running work, deadlines and recent output at a glance.', keywords: ['home', 'overview'] },
      { to: '/matters', label: 'Matters', icon: 'Library', gated: true, description: 'The case hub: documents, dockets, deadlines and work product.', keywords: ['case', 'client', 'file'] },
      { to: '/documents', label: 'Document Generator', icon: 'Doc', gated: true, description: 'Intake to drafted document — and the library of everything generated so far.', keywords: ['documents', 'drafts', 'output', 'library'] },
      { to: '/facts', label: 'Facts', icon: 'Facts', gated: true, description: 'Your uploaded record — indexed and searchable for grounding.', keywords: ['upload', 'evidence', 'files'] },
      { to: '/watches', label: 'Watches & Alerts', icon: 'Bell', gated: true, description: 'Authorities you track, and what changed under them.', keywords: ['tracker', 'notifications', 'inbox', 'alerts'] },
    ],
  },
  {
    label: 'Research',
    items: [
      { to: '/research', label: 'Research Agent', icon: 'Sparkle', gated: true, description: 'A bounded research run with a full trail and a gated memo.', keywords: ['agent', 'memo', 'issue'] },
      { to: '/precedent', label: 'Precedent Finder', icon: 'Library', gated: true, description: 'Fact pattern in, on-point authority out.', keywords: ['cases', 'authority', 'on point'] },
      { to: '/citator', label: 'Citator', icon: 'Scale', gated: true, description: 'Who cites this case, how, and is it still good law.', keywords: ['keycite', 'shepardize', 'treatment', 'good law'] },
      { to: '/headnotes', label: 'Headnote Generator', icon: 'Doc', gated: true, description: 'Turn an opinion into structured headnotes.', keywords: ['syllabus', 'summary'] },
      { to: '/firm-brain', label: 'Firm Brain', icon: 'Search', gated: true, description: "Search your firm's own prior work product.", keywords: ['knowledge', 'precedent bank', 'briefed before'] },
    ],
  },
  {
    label: 'Draft',
    items: [
      { to: '/drafter', label: 'Drafting Assistant', icon: 'Content', gated: true, description: 'Fact pattern to an audited argument draft, section by section.', keywords: ['brief', 'motion', 'argument', 'write'] },
      { to: '/deconstructor', label: 'Brief Deconstructor', icon: 'Audit', gated: true, description: "Find the weaknesses in the other side's brief.", keywords: ['opposition', 'attack', 'response', 'weakness'] },
      { to: '/gazette', label: 'Gazette', icon: 'Send', gated: true, description: 'Generate a complete newsletter issue from live sources.', keywords: ['newsletter', 'client alert', 'email'] },
    ],
  },
  {
    label: 'Review',
    items: [
      { to: '/cite-check', label: 'Cite-Check Editor', icon: 'Check', gated: true, description: 'Live citation verification as you type.', keywords: ['verify', 'citations', 'proof'] },
      { to: '/brief-audit', label: 'Brief Auditor', icon: 'Shield', gated: true, description: 'Existence, good-law status and quote fidelity for every cite.', keywords: ['audit', 'hallucination', 'quotes'] },
      { to: '/colloquy', label: 'Colloquy', icon: 'Users', gated: true, description: 'Multi-model peer review with a per-comment decision log.', keywords: ['peer review', 'critique', 'second opinion'] },
      { to: '/redline', label: 'Redline / Compare', icon: 'Content', gated: true, description: 'Compare two versions and see exactly what moved.', keywords: ['diff', 'compare', 'changes', 'markup'] },
      { to: '/analyzer', label: 'Document Analyzer', icon: 'Search', gated: true, description: 'Pull the structure, risks and obligations out of a document.', keywords: ['review', 'contract', 'risks'] },
      { to: '/summarizer', label: 'Summarizer', icon: 'Summarize', gated: true, description: 'Condense long records into something readable.', keywords: ['summary', 'digest', 'tl;dr'] },
    ],
  },
  {
    label: 'Case tools',
    items: [
      { to: '/deadlines', label: 'Deadline Calculator', icon: 'Clock', gated: true, description: 'Court-rules deadlines computed from a trigger date.', keywords: ['dates', 'calendar', 'rules', 'compute'] },
      { to: '/chronology', label: 'Chronology Builder', icon: 'History', gated: true, description: 'Build a sourced timeline from the record.', keywords: ['timeline', 'events', 'facts'] },
      { to: '/redaction', label: 'Redaction', icon: 'Trash', gated: true, description: 'Strip identifiers before a document leaves the firm.', keywords: ['pii', 'anonymize', 'privacy'] },
      { to: '/valorem', label: 'Valorem', icon: 'Pulse', gated: true, description: 'Patent valuation: evidence, comparables and modelled range.', keywords: ['patent', 'valuation', 'damages', 'price'] },
      { to: '/orgs', label: 'Organization', icon: 'Users', gated: true, description: 'Members, roles and the org whose work you share.', keywords: ['team', 'firm', 'members', 'sharing'] },
    ],
  },
];

/** Flat view for anything that needs the full list (palette, breadcrumbs, tests). */
export const TOOL_NAV: NavItem[] = TOOL_GROUPS.flatMap((g) => g.items);

export const ADMIN_NAV: NavItem[] = [
  { to: '/admin/users', label: 'Users', icon: 'Users', admin: true, description: 'Accounts, roles and status.' },
  { to: '/admin/audit', label: 'Audit Log', icon: 'Audit', admin: true, description: 'Who did what, and when.' },
  { to: '/engines', label: 'AI Engines', icon: 'Engine', staff: true, description: 'The providers, models and chains available.' },
  { to: '/admin/ai', label: 'AI Providers', icon: 'Engine', staff: true, description: 'Configure providers, models and chains.' },
  { to: '/admin/library', label: 'Research Library', icon: 'Library', staff: true, description: 'The indexed corpus behind retrieval.' },
  { to: '/admin/layers', label: 'Enhancement Layers', icon: 'Layers', staff: true, description: 'Prompt, fact, research, form and template layers.' },
  { to: '/admin/content', label: 'Forms & Templates', icon: 'Content', staff: true, description: 'Intake schemas and document templates.' },
  { to: '/admin/ingestion', label: 'Ingestion', icon: 'Ingest', staff: true, description: 'Corpus ingestion jobs and their items.' },
  { to: '/admin/connectors', label: 'Source Connectors', icon: 'Ingest', staff: true, description: 'External corpus feeds and their cursors.' },
  { to: '/admin/valorem', label: 'Valorem', icon: 'Pulse', staff: true, description: 'Fields, scenarios, sources, weights and models.' },
  { to: '/admin/deadline-packs', label: 'Court-Rules Packs', icon: 'Clock', staff: true, description: 'The rule packs the calculator resolves against.' },
];

/** Always reachable, signed in or not. */
export const UTILITY_NAV: NavItem[] = [
  { to: '/settings', label: 'Account', icon: 'Users', gated: true, description: 'Profile, password and appearance.', keywords: ['profile', 'password', 'settings', 'theme'] },
  { to: '/status', label: 'System Status', icon: 'Activity', description: 'Connection, services and the client diagnostics log.', keywords: ['health', 'diagnostics', 'logs', 'debug'] },
  { to: '/contact', label: 'Contact', icon: 'Send', description: 'Questions, support requests and feedback.', keywords: ['support', 'help', 'email'] },
];

/**
 * The first path segment identifies the page family; the chat landing page (/)
 * and /chat/:id are the same family. Used to key the per-page error boundary so
 * list-to-item navigation does not remount a page mid-stream.
 */
export function pageFamily(pathname: string): string {
  const first = pathname.split('/')[1] ?? '';
  return first === '' ? 'chat' : first;
}
